<template>
  <div class="resume-modal-wrapper"> 
    <vs-prompt
      class="resume-upload-prompt"
      :title="modalTitle"
      :active.sync="isModalActive"
      @close="close"
      buttons-hidden
    >
      
      <div class="upload-container" style="position: relative;">
        
        <!-- SILENT MODE TOGGLE (GUARANTEED POSITION & COLOR) -->
        <div style="position: absolute; right: 20px; top: -52px; z-index: 1000;">
          <vs-tooltip :text="isSilentMode ? 'Silent Mode On (No Emails)' : 'Silent Mode Off (Emails Active)'">
            <div 
              @click="isSilentMode = !isSilentMode; persistState()"
              style="cursor: pointer; padding: 5px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease;"
              :style="{ color: isSilentMode ? '#ff9f43' : '#a0a0a0' }"
            >
              <!-- BELL OFF ICON (Silent Mode) -->
              <svg v-if="isSilentMode" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                <path d="M18.63 13A17.89 17.89 0 0 1 18 8"></path>
                <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14"></path>
                <path d="M18 8a6 6 0 0 0-9.33-5"></path>
                <line x1="1" y1="1" x2="23" y2="23"></line>
              </svg>

              <!-- BELL ON ICON (Active Mode) -->
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
            </div>
          </vs-tooltip>
        </div>
        
        <input
          type="file"
          ref="fileInput"
          @change="onFileSelect" 
          multiple
          accept=".pdf,.doc,.docx,.txt"
          hidden
        />

        <!-- STATE 1: INITIAL UPLOAD (No batches yet) -->
        <div v-if="localBatchIds.length === 0 && uploadQueue.length === 0" class="h-full">
          <div
            class="drop-zone"
            :class="{ 'dragover': isDragging }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onDrop"
            @click="openFileDialog"
          >
            <div>
              <p class="font-semibold text-lg">{{ $t('orgDashboard.talentPool.draganddropPopup') }}</p>
              <p class="text-sm text-gray mt-1">{{ $t('orgDashboard.talentPool.Clicktoselectfiles') }}</p>
            </div>
          </div>
          <div class="text-center mt-4 text-sm text-gray pb-2">
            <p>{{ $t('orgDashboard.talentPool.Supportedfileformats') }}</p>
            <p>{{ $t('orgDashboard.talentPool.Maxuploadsize') }}</p>
          </div>
        </div>

        <!-- STATE 2: PROCESSING / RESULTS -->
        <div v-else class="processing-view">
          
          <div>
            <!-- Header: Overall Stats -->
            <div class="mb-4 flex justify-between items-end border-b pb-3">
              <div>
                <h4 class="font-bold text-dark mb-1">{{ progressTitle }}</h4>
                <p class="text-sm text-gray-600">
                  {{ aggregatedStatus.processed_count }} / {{ aggregatedStatus.total_files }} {{ $t('orgDashboard.talentPool.processed') }}
                </p>
                <!-- Queue Indicator -->
                <p v-if="uploadQueue.length > 0" class="text-xs text-warning font-semibold mt-1">
                   <vs-icon icon="feather:clock" size="10px"></vs-icon> {{ uploadQueue.length }} {{ $t('orgDashboard.talentPool.Batches_in_queue') }}
                </p>
              </div>
              <div class="text-right">
                <span class="text-xs text-gray block">{{ $t('orgDashboard.talentPool.total_progress') }}</span>
                <span class="font-bold text-primary text-3xl">{{ Math.floor(calculatedPercentage) }}%</span>
              </div>
            </div>

            <!-- Scrollable List with INDIVIDUAL Progress Bars -->
            <vue-perfect-scrollbar class="file-list-container" :settings="scrollbarSettings">
              <div v-for="(file, filename) in aggregatedStatus.files" :key="filename" class="file-item-wrapper">
                
                <div class="file-item-header flex justify-between items-center mb-1">
                  <div class="flex items-center overflow-hidden">
                    <vs-icon icon="feather:file-text" size="14px" class="mr-2 text-gray"></vs-icon>
                    <span class="filename font-medium text-sm truncate" :title="filename">{{ filename }}</span>
                  </div>
                  
                  <!-- Status Badge / Percentage Text -->
                  <div class="flex-shrink-0 ml-2">
                     <span v-if="file.status === 'completed' || file.status === 'processed_and_emailed'" class="text-success text-xs font-bold flex items-center">
                        {{ $t('orgDashboard.talentPool.done') }} <vs-icon icon="feather:check" size="10px" class="ml-1"></vs-icon>
                     </span>
                     <span v-else-if="file.status === 'failed'" class="text-danger text-xs font-bold flex items-center">
                        {{ $t('orgDashboard.talentPool.failed') }} <vs-icon icon="feather:x" size="10px" class="ml-1"></vs-icon>
                     </span>
                     <span v-else class="text-warning text-xs font-bold">
                        {{ Math.floor(file.progress || 0) }}%
                     </span>
                  </div>
                </div>

                <!-- MINI PROGRESS BAR FOR THIS SPECIFIC FILE -->
                <div class="mini-progress-track">
                    <div 
                        class="mini-progress-fill" 
                        :class="getFileStatusClass(file.status)"
                        :style="{ width: (file.status === 'completed' || file.status === 'failed' ? 100 : (file.progress || 0)) + '%' }"
                    ></div>
                </div>

              </div>
            </vue-perfect-scrollbar>

            <!-- Success/Fail Summary -->
            <div v-if="isComplete" class="summary-box mt-4 fade-in">
              <p class="text-sm">
                Process finished: 
                <span class="text-success font-semibold">{{ successCount }} successful</span>,
                <span class="text-danger font-semibold">{{ failureCount }} failed</span>.
              </p>
            </div>

            <!-- Footer Actions -->
            <div class="w-full flex flex-col justify-center mt-5">
              
              <!-- Buttons while Processing -->
              <button
                  v-if="!isComplete"
                  class="custom-capsule-btn w-full mb-3"
                  @click="openFileDialog"
                  :disabled="isUploading"
                  :style="isUploading ? 'opacity: 0.5; cursor: not-allowed;' : ''"
              >
                  {{ isUploading ? $t('orgDashboard.talentPool.Uploading_batch') : $t('orgDashboard.talentPool.Upload_More_Resumes') }}
              </button>

              <div v-if="!isComplete" class="text-center">
                  <span 
                      @click="close" 
                      class="text-xs text-gray cursor-pointer hover:underline"
                  >
                      Hide & Process in Background
                  </span>
              </div>

              <!-- Button when Complete -->
              <vs-button
                  v-else
                  class="w-full"
                  color="orange"
                  type="filled"
                  @click="finishProcess"
              >
                  {{ $t('orgDashboard.talentPool.finish') }}
              </vs-button>
            </div>
          </div>
        </div>
      </div>
    </vs-prompt>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';

const API_BASE_URL = process.env.VUE_APP_API_URL;
const MAX_FILES = 10;
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt'];

export default {
  name: "ResumeUploadModal",
  props: {
    organizationId: { type: String, required: true }
  },
  components: { VuePerfectScrollbar },
  data() {
    return {
      isModalActive: true,
      isDragging: false,
      isSilentMode: false,
      scrollbarSettings: { suppressScrollX: true },
      
      localBatchIds: [],
      batchesData: {}, // Stores data for all batches
      
      // QUEUE LOGIC VARIABLES
      uploadQueue: [], // Stores pending file batches
      isUploading: false, 
      
      pollingInterval: null,
      calculatedPercentage: 0, 
      stateKey: null,
      hasNotifiedCompletion: false
    };
  },
  computed: {
    ...mapState('organization', ['activeResumeBatchId']),

    aggregatedStatus() {
        let totalFiles = 0;
        let processedCount = 0;
        let allFiles = {};
        let statuses = [];

        // 1. Process Active Batches (From Backend)
        this.localBatchIds.forEach(bid => {
            const data = this.batchesData[bid];
            if (data) {
                totalFiles += (data.total_files || 0);
                processedCount += (data.processed_count || 0);
                if (data.files) Object.assign(allFiles, data.files);
                statuses.push(data.status);
            }
        });

        // 2. Process Queued Batches (Local Pending)
        // We display them in the list with "Waiting..." status
        this.uploadQueue.forEach(item => {
           item.files.forEach(f => {
              // Create a temp key so they show up in the list
              allFiles[`(Queue) ${f.name}`] = { status: 'queued', progress: 0 };
              totalFiles++;
           });
           statuses.push('pending');
        });

        let globalStatus = 'pending';
        // Only "Completed" if NO queue remains AND all backend batches are done
        if (this.localBatchIds.length > 0 && this.uploadQueue.length === 0) {
            const allDone = statuses.every(s => ['completed', 'completed_with_errors', 'failed'].includes(s));
            globalStatus = (allDone && statuses.length > 0) ? 'completed' : 'processing';
        }

        return {
            status: globalStatus,
            total_files: totalFiles,
            processed_count: processedCount,
            files: allFiles
        };
    },
    
    modalTitle() {
      return (this.localBatchIds.length === 0 && this.uploadQueue.length === 0) ? this.$t('orgDashboard.talentPool.importResumes') : this.$t('orgDashboard.talentPool.processingStatus');
    },

    isComplete() {
      return this.localBatchIds.length > 0 && this.uploadQueue.length === 0 && this.aggregatedStatus.status === 'completed';
    },

    progressTitle() {
      if (this.isComplete) return this.$t('orgDashboard.talentPool.processingComplete');
      if (this.uploadQueue.length > 0) return this.$t('orgDashboard.talentPool.uploadingAndParsing');
      return this.$t('orgDashboard.talentPool.parsingProfiles');
    },

    successCount() {
        return Object.values(this.aggregatedStatus.files).filter(f => f.status === 'completed' || f.status === 'processed_and_emailed').length;
    },

    failureCount() {
        return Object.values(this.aggregatedStatus.files).filter(f => f.status === 'failed').length;
    }
  },
  watch: {
    // TRIGGER NEXT BATCH
    uploadQueue: {
        handler(val) {
            if (val.length > 0 && !this.isUploading) {
                this.processNextInQueue();
            }
        },
        deep: true
    },

    // SYNC EXTERNAL BATCH ID
    activeResumeBatchId: {
        immediate: true,
        handler(newVal) {
            if(newVal && !this.localBatchIds.includes(newVal)) {
                if (!this.isComplete) {
                    this.localBatchIds.push(newVal);
                    this.startPolling();
                }
            }
        }
    },
    
    // ORGANIZATION SWITCH
    organizationId: {
        immediate: true,
        handler(newVal, oldVal) {
            if (oldVal && newVal !== oldVal) {
                this.persistState(); 
                this.stateKey = `resume_upload_state_${newVal}`;
                this.localBatchIds = [];
                this.batchesData = {};
                this.uploadQueue = [];
                this.loadState(); 
            }
        }
    },
    
    // CLOSE MODAL HANDLER
    isModalActive(val) {
        if (!val) {
            this.close();
        }
    }
  },
  
  mounted() {
    this.stateKey = `resume_upload_state_${this.organizationId}`;
    this.loadState();
    if (this.localBatchIds.length > 0 && !this.isComplete) {
      this.startPolling();
    }
  },
  
  beforeDestroy() {
    if (!this.isComplete) this.persistState();
    this.stopPolling();
  },
  
  methods: {
    getFileStatusClass(status) {
        if(status === 'completed' || status === 'processed_and_emailed') return 'bg-success';
        if(status === 'failed') return 'bg-danger';
        if(status === 'queued') return 'bg-gray-300';
        return 'bg-warning';
    },

    loadState() {
      try {
        const saved = localStorage.getItem(this.stateKey);
        if (!saved) return;
        
        // 'state' is created here
        const state = JSON.parse(saved);
        
        if (Date.now() - (state.timestamp || 0) > 3600000) {
             localStorage.removeItem(this.stateKey);
             return;
        }

        const tempStatuses = [];
        if(state.batchesData) {
            Object.values(state.batchesData).forEach(b => tempStatuses.push(b.status));
        }
        const wasAllDone = tempStatuses.length > 0 && tempStatuses.every(s => ['completed', 'failed'].includes(s));

        if (wasAllDone) {
            this.resetLocalData();
            localStorage.removeItem(this.stateKey);
            return;
        }

        this.localBatchIds = state.localBatchIds || [];
        this.batchesData = state.batchesData || {};
        this.calculatedPercentage = state.calculatedPercentage || 0;
        this.uploadQueue = state.uploadQueue || [];
        
        // ---> THIS IS THE CORRECT SPOT FOR SILENT MODE <---
        this.isSilentMode = state.isSilentMode || false; 
        
        if(this.uploadQueue.length > 0 && !this.isUploading) {
            this.processNextInQueue();
        }

      } catch (e) { 
          console.error(e); 
      }
    },

    persistState() {
        if(this.localBatchIds.length === 0 && this.uploadQueue.length === 0) return;
        const state = {
            localBatchIds: this.localBatchIds,
            batchesData: this.batchesData,
            calculatedPercentage: this.calculatedPercentage,
            uploadQueue: this.uploadQueue,
            isSilentMode: this.isSilentMode, // <--- Silent mode is here
            timestamp: Date.now()
        };
        localStorage.setItem(this.stateKey, JSON.stringify(state));
    },

    // --- REAL-TIME CALCULATION LOGIC ---
    updateGlobalProgress() {
      if (this.isComplete) {
        this.calculatedPercentage = 100;
        this.$store.dispatch('organization/updateBatchProgress', 100);
        return;
      }
      
      let totalAccumulatedPoints = 0;
      let totalItems = 0;
      
      // 1. Calculate Active Files (From Backend Data)
      Object.values(this.batchesData).forEach(batch => {
        if (batch.files) {
          Object.values(batch.files).forEach(file => {
            totalItems++;
            // STRICT: Use backend progress. If completed/failed, force 100.
            if (file.status === 'completed' || file.status === 'processed_and_emailed' || file.status === 'failed') {
                totalAccumulatedPoints += 100;
            } else {
                totalAccumulatedPoints += (file.progress || 0);
            }
          });
        }
      });

      // 2. Calculate Queued Files (From Frontend Queue)
      // They count towards total Items, but add 0 to accumulated points.
      this.uploadQueue.forEach(q => {
          totalItems += q.files.length;
      });
      
      // 3. Math: Average
      if (totalItems > 0) {
         this.calculatedPercentage = totalAccumulatedPoints / totalItems;
      } else {
         this.calculatedPercentage = 0;
      }

      this.$store.dispatch('organization/updateBatchProgress', this.calculatedPercentage);
      this.persistState();
    },

    async fetchStatuses() {
      if (this.localBatchIds.length === 0) return;
      try {
        const batchIdsParam = this.localBatchIds.join(',');
        const response = await fetch(`${API_BASE_URL}/status/multi-batch?batch_ids=${batchIdsParam}&organization_id=${this.organizationId}`);
        
        if (response.ok) {
          const data = await response.json();
          if (data.batches) {
            Object.keys(data.batches).forEach(bid => {
                const incoming = data.batches[bid];
                // Overwrite local data with Backend Data
                this.$set(this.batchesData, bid, incoming);
            });
          }
          
          // Re-calculate math based on new data
          this.updateGlobalProgress();

          if (this.isComplete) {
            this.stopPolling();
            this.calculatedPercentage = 100;
            if(!this.hasNotifiedCompletion) {
                this.$vs.notify({title:'Success', text:'Parsing Complete', color:'success'});
                this.hasNotifiedCompletion = true;
            }
          }
        }
      } catch (e) { console.error(e); }
    },

    openFileDialog() { if(this.$refs.fileInput) this.$refs.fileInput.click(); },
    
    onFileSelect(e) { if(e.target.files) this.handleFiles(e.target.files); },
    onDrop(e) { this.isDragging = false; if(e.dataTransfer.files) this.handleFiles(e.dataTransfer.files); },

    // STEP 1: Add to Queue
    handleFiles(files) {
      const validFiles = Array.from(files).filter(f => ALLOWED_EXTENSIONS.some(ext => f.name.toLowerCase().endsWith(ext)));
      if (validFiles.length === 0 || validFiles.length > MAX_FILES) return this.$vs.notify({title:'Invalid', text:'Check formats/limit', color:'warning'});
      
      // Add to Queue
      this.uploadQueue.push({
          files: validFiles,
          tempId: Date.now() + Math.random()
      });
      
      // Update UI immediately (sets denominator for progress bar)
      this.updateGlobalProgress(); 
      if(this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    // STEP 2: Process Queue Sequentially
    async processNextInQueue() {
        if (this.uploadQueue.length === 0 || this.isUploading) return;

        this.isUploading = true;
        const currentBatch = this.uploadQueue[0]; 

        // We do NOT show global loading spinner here because user might be looking at progress bar
        // Instead we lock the button in UI
        
        const formData = new FormData();
        formData.append('api_token', localStorage.getItem('accessToken'));
        formData.append('organization_id', this.organizationId);
        formData.append('silent_mode', this.isSilentMode);
        const kvdrId = this.$store.state.organization.organizationConfiguration?._id || this.organizationId;
        formData.append("kvdr_id", kvdrId);

        currentBatch.files.forEach(f => formData.append('files', f));

        try {
            const res = await fetch(`${API_BASE_URL}/upload-resumes-batch/`, { method: 'POST', body: formData });
            const data = await res.json();
            if(!res.ok) throw new Error(data.detail);

            const newId = data.batch_id;

            // Initialize Batch Data with 5% (Upload Complete)
            const fileObj = {};
            currentBatch.files.forEach(f => fileObj[f.name] = { status: 'processing', progress: 5 });

            this.$set(this.batchesData, newId, {
                status: 'processing',
                total_files: currentBatch.files.length,
                processed_count: 0,
                files: fileObj
            });

            if(!this.localBatchIds.includes(newId)) this.localBatchIds.push(newId);

            // Remove from queue
            this.uploadQueue.shift();

            this.persistState();
            this.$store.dispatch('organization/startResumeBatch', newId);

            this.startPolling();

        } catch (e) {
            this.$vs.notify({title:'Upload Error', text:e.message, color:'danger'});
            this.uploadQueue.shift(); // Remove failed batch to unblock queue
        } finally {
            this.isUploading = false;
        }
    },

    startPolling() { if(!this.pollingInterval) this.pollingInterval = setInterval(this.fetchStatuses, 2000); },
    stopPolling() { if(this.pollingInterval) { clearInterval(this.pollingInterval); this.pollingInterval = null; } },

    close() {
      this.persistState();
      this.$emit('close');
    },

    resetLocalData() {
        this.localBatchIds = [];
        this.batchesData = {};
        this.uploadQueue = [];
        this.calculatedPercentage = 0;
        this.hasNotifiedCompletion = false;
        this.isUploading = false;
    },

    finishProcess() {
      this.stopPolling();
      this.resetLocalData();
      localStorage.removeItem(this.stateKey);
      this.$store.dispatch('organization/clearResumeBatch');
      if(this.$refs.fileInput) this.$refs.fileInput.value = '';
      this.isModalActive = false; 
      this.$emit('upload-complete');
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.text-primary { color: var(--vs-primary); }
.text-success { color: var(--vs-success); }
.text-danger  { color: var(--vs-danger); }
.bg-success   { background-color: var(--vs-success) !important; }
.bg-danger    { background-color: var(--vs-danger) !important; }
.bg-warning   { background-color: #FFC107 !important; }
.bg-gray-300  { background-color: #cbd5e1 !important; } /* Queued Color */

.custom-capsule-btn {
  background: transparent; color: #FFC107; border: 2px solid #FFC107;
  border-radius: 50px; padding: 10px; font-weight: 600; cursor: pointer;
  transition: all 0.3s;
}
.custom-capsule-btn:hover { background: #FFC107; color: white; }
.custom-capsule-btn:disabled { border-color: #ccc; color: #ccc; background: transparent; }

.drop-zone {
  border: 2px dashed #e2e8f0; border-radius: 16px; background: #f8fafc;
  display: flex; flex-direction: column; justify-content: center; align-items: center;
  min-height: 300px; cursor: pointer; transition: all 0.3s;
}
.drop-zone:hover { background: #f1f5f9; border-color: #94a3b8; }

.file-list-container { max-height: 250px; padding-right: 5px; }
.file-item-wrapper { padding: 10px 0; border-bottom: 1px solid #f1f1f1; }
.filename { max-width: 60%; }

/* Mini Progress Bar Styles */
.mini-progress-track { width: 100%; height: 4px; background: #f0f2f5; border-radius: 2px; overflow: hidden; }
.mini-progress-fill { height: 100%; transition: width 0.3s ease; }
.fade-in { animation: fadeIn 0.5s ease-in; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
</style>